import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  LinearProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { facilityService } from "../../services/facilityService";
import { useAuth } from "../../contexts/AuthContext";

const EditFacility = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    company_name: "",
    phone_number: "",
    address: "",
    email: "",
    fax: "",
    business_license: "",
    npi: "",
    credit_card_last4: "",
    billing_address: "",
    subscription_type: "basic",
    admin_level: 1,
  });

  // Subscription types and admin levels
  const subscriptionTypes = [
    { value: "basic", label: "Basic" },
    { value: "standard", label: "Standard" },
    { value: "premium", label: "Premium" },
  ];

  const adminLevels = [
    { value: 1, label: "Basic" },
    { value: 2, label: "Standard" },
    { value: 3, label: "Advanced" },
    { value: 4, label: "Super Admin" },
  ];

  useEffect(() => {
    // Check permissions
    if (!canEditFacility()) {
      setError("You don't have permission to edit this facility");
      return;
    }

    fetchFacilityDetails();
  }, [id]);

  const canEditFacility = () => {
    return isSuperAdmin || user?.facility_id === parseInt(id);
  };

  const fetchFacilityDetails = async () => {
    setLoading(true);
    try {
      const response = await facilityService.getFacilityById(id);
      if (response) {
        setFacility(response);
        setFormData({
          company_name: response.company_name || "",
          phone_number: response.phone_number || "",
          address: response.address || "",
          email: response.email || "",
          fax: response.fax || "",
          business_license: response.business_license || "",
          npi: response.npi || "",
          credit_card_last4: response.credit_card_last4 || "",
          billing_address: response.billing_address || "",
          subscription_type: response.subscription_type || "basic",
          admin_level: response.admin_level || 1,
        });
      } else {
        setError("Facility not found");
      }
    } catch (err) {
      console.error("Error fetching facility details:", err);
      setError("Failed to load facility details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const validation = facilityService.validateFacilityData(formData);
    setFieldErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    // Validate form data
    if (!validateForm()) {
      setSaving(false);
      setError("Please fix the validation errors below");
      return;
    }

    try {
      const updateData = { ...formData };

      // Only super admin can change admin level and subscription type
      if (!isSuperAdmin) {
        delete updateData.admin_level;
        delete updateData.subscription_type;
      }

      const response = await facilityService.updateFacility(id, updateData);

      setFacility(response);
      setSuccess("Facility information updated successfully");

      // Redirect to detail view after successful update
      setTimeout(() => {
        navigate(`/facilities/${id}`);
      }, 1500);
    } catch (err) {
      console.error("Error updating facility:", err);
      setError(
        err.message ||
          err.response?.data?.detail ||
          "Failed to update facility information"
      );
    } finally {
      setSaving(false);
    }
  };

  const getSubscriptionColor = (subscriptionType) => {
    const colors = {
      basic: "default",
      standard: "primary",
      premium: "secondary",
    };
    return colors[subscriptionType?.toLowerCase()] || "default";
  };

  const formatSubscription = (subscriptionType) => {
    if (!subscriptionType) return "N/A";
    return subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1);
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !facility) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/facilities")}
        >
          Back to Facilities
        </Button>
      </Box>
    );
  }

  if (!facility) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Facility not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/facilities")}
          sx={{ mt: 2 }}
        >
          Back to Facilities
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to={`/facilities/${id}`}
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Details
          </Button>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Edit Facility
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Typography variant="h6" color="text.secondary">
                {facility.company_name || "Facility"}
              </Typography>
              <Chip
                label={formatSubscription(facility.subscription_type)}
                color={getSubscriptionColor(facility.subscription_type)}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid  size={{ xs: 12, md: 8 }}>
            {/* Company Information */}
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Company Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    error={!!fieldErrors.company_name}
                    helperText={fieldErrors.company_name}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    required
                    error={!!fieldErrors.phone_number}
                    helperText={fieldErrors.phone_number}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fax Number"
                    name="fax"
                    value={formData.fax}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={2}
                    error={!!fieldErrors.address}
                    helperText={fieldErrors.address}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Licensing Information */}
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Licensing Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Business License"
                    name="business_license"
                    value={formData.business_license}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="NPI Number"
                    name="npi"
                    value={formData.npi}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Billing Information */}
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Billing Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Credit Card Last 4 Digits"
                    name="credit_card_last4"
                    value={formData.credit_card_last4}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 4 }}
                    helperText="Only last 4 digits for security"
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Billing Address"
                    name="billing_address"
                    value={formData.billing_address}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    helperText="Leave empty to use company address"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid  size={{ xs: 12, md: 4 }}>
            {/* Subscription & Admin Settings */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Subscription & Access
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid  size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Subscription Type</InputLabel>
                    <Select
                      name="subscription_type"
                      value={formData.subscription_type}
                      onChange={handleInputChange}
                      label="Subscription Type"
                      disabled={!isSuperAdmin}
                    >
                      {subscriptionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!isSuperAdmin && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Only super admins can change subscription type
                    </Typography>
                  )}
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Admin Level</InputLabel>
                    <Select
                      name="admin_level"
                      value={formData.admin_level}
                      onChange={handleInputChange}
                      label="Admin Level"
                      disabled={!isSuperAdmin}
                    >
                      {adminLevels.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!isSuperAdmin && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Only super admins can change admin level
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Current Settings Info */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subscription
                  </Typography>
                  <Chip
                    label={formatSubscription(facility.subscription_type)}
                    color={getSubscriptionColor(facility.subscription_type)}
                    size="small"
                  />
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Admin Level
                  </Typography>
                  <Typography variant="body2">
                    {adminLevels.find(
                      (level) => level.value === facility.admin_level
                    )?.label || "Unknown"}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Facility ID
                  </Typography>
                  <Typography variant="body2">{facility.id}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {facility.updated_at
                      ? new Date(facility.updated_at).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          mt={4}
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.default",
            py: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate(`/facilities/${id}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditFacility;
